import * as Yup from "yup";
import { getOptions } from "@features/dropdownOptions/fetchOptions";
import type { ActivityUpdatePayload } from "@features/activities/activityTypes";
import type {
	CalendarYearSelectGroup,
	ClassicInputGroup,
	DropdownInputGroup,
} from "@typedefs/modulesInterfaces";
import { FieldType } from "@typedefs/modulesInterfaces";
import type { Activity, Project } from "@typedefs/interfaces";
import { validatePositiveNumber } from "@pages/activityBuilder/modules/moduleUtils";
import { getCapitalizationYears } from "@utils/helperFunctions";

/**
 * Single source of truth for the activity form shared by
 *  - the creation flow (setup assistant, first step), and
 *  - the edit activity page.
 * Fields, validation rules, initial values and the form -> API mapping live here,
 * so any change to an activity field rule must be made in this file only.
 */

export type ActivityFormValues = ActivityUpdatePayload;
export type ActivityFormActivity = ActivityFormValues["activity"];

/** The subset of the project the activity form depends on. */
export type ActivityFormProject = Pick<
	Project,
	"climate" | "moisture" | "implementation_years" | "start_year_of_activities" | "last_year_of_accounting" | "cost"
>;

export enum ActivityFormFields {
	name = "activity.name",
	duration_t2 = "activity.duration_t2",
	climate_t2 = "activity.climate_t2",
	moisture_t2 = "activity.moisture_t2",
	soc_t2 = "activity.soc_t2",
	start_year_t2 = "activity.start_year_t2",
	cost = "activity.cost",
	change_rate = "activity.change_rate",
	capitalization_years = "activity.capitalization_years",
	last_year_of_accounting_t2 = "activity.last_year_of_accounting_t2",
}

// ---------------------------------------------------------------- fields ---

export const activityTitle: ClassicInputGroup = {
	type: FieldType.CLASSIC,
	inputName: ActivityFormFields.name,
	inputType: "text",
	label: "activity.activity_title",
};
export const duration: ClassicInputGroup = {
	type: FieldType.CLASSIC,
	inputName: ActivityFormFields.duration_t2,
	inputType: "number",
	label: "activity.duration",
	unit: "[years]",
};
export const startYear: CalendarYearSelectGroup = {
	type: FieldType.CALENDAR_YEAR_SELECT,
	inputName: ActivityFormFields.start_year_t2,
	label: "activity.start_year",
	unit: "[years]",
	startYear: 1900,
	endYear: 2100,
};
export const climate: DropdownInputGroup = {
	type: FieldType.SELECT,
	inputName: ActivityFormFields.climate_t2,
	label: "activity.climate",
	dropdownOptions: () => getOptions("climate"),
};
export const moisture: DropdownInputGroup = {
	type: FieldType.SELECT,
	inputName: ActivityFormFields.moisture_t2,
	label: "activity.moisture",
	dropdownOptions: [],
};
export const socT2: ClassicInputGroup = {
	type: FieldType.CLASSIC,
	inputName: ActivityFormFields.soc_t2,
	inputType: "number",
	label: "module.tier_two",
	textend: true,
};
export const dynamicOfChanges: DropdownInputGroup = {
	type: FieldType.SELECT,
	inputName: ActivityFormFields.change_rate,
	label: "activity.dynamic_of_changes",
	dropdownOptions: () => getOptions("changeRates"),
};
export const activityCost: ClassicInputGroup = {
	type: FieldType.CLASSIC,
	inputName: ActivityFormFields.cost,
	inputType: "number",
	label: "activity.activity_cost",
	unit: "[USD]",
};
export const capitalizationPhase: ClassicInputGroup = {
	type: FieldType.CLASSIC,
	inputName: ActivityFormFields.capitalization_years,
	inputType: "number",
	label: "project.capitalization_years",
	textend: true,
	disabled: true,
};
export const lastYearOfAccounting: CalendarYearSelectGroup = {
	type: FieldType.CALENDAR_YEAR_SELECT,
	inputName: ActivityFormFields.last_year_of_accounting_t2,
	label: "activity.last_year_of_accounting_t2",
	unit: "[years]",
	startYear: 1900,
	endYear: 2100,
};

// ------------------------------------------------------------ validation ---

export const createActivityValidationSchema = (project: ActivityFormProject) =>
	Yup.object({
		activity: Yup.object({
			name: Yup.string().required("validations.activity_name_required"),
			duration_t2: validatePositiveNumber
				.required("validations.duration_required")
				.min(1, "validations.duration_min")
				.test(
					"duration-within-project",
					"validations.activity_start_year_duration_check",
					function (value) {
						const { start_year_t2 } = this.parent;
						return (
							(start_year_t2 as number) + (value as number) <=
							project.start_year_of_activities + project.implementation_years
						);
					},
				),
			climate_t2: validatePositiveNumber.required("validations.climate_required"),
			moisture_t2: validatePositiveNumber.required("validations.moisture_required"),
			soc_t2: validatePositiveNumber.nullable(),
			start_year_t2: validatePositiveNumber
				.required()
				.min(1900)
				.test(
					"start-year-after-project",
					"validations.activity_start_year",
					(value) => (value as number) >= project.start_year_of_activities,
				),
			last_year_of_accounting_t2: validatePositiveNumber
				.required("validations.last_year_of_accounting_required")
				.test(
					"last-year-after-implementation",
					"validations.activity_last_year_of_accounting2",
					function (value) {
						const { duration_t2, start_year_t2 } = this.parent;
						return (value as number) >= start_year_t2 + duration_t2;
					},
				)
				.test(
					"last-year-before-project-end",
					"validations.activity_last_year_of_accounting",
					(value) => (value as number) <= project.last_year_of_accounting,
				),
			change_rate: validatePositiveNumber.required("validations.change_rate_required"),
			cost: validatePositiveNumber
				.required()
				.test(
					"cost-within-project",
					"validations.activity_cost_less_than_project",
					(value) => project.cost == null || (value as number) <= project.cost,
				),
			capitalization_years: validatePositiveNumber.min(0, "validations.cap_phase_min"),
		}),
	});

// -------------------------------------------------------- initial values ---

type ActivitySource = Pick<
	Activity,
	| "name"
	| "duration_t2"
	| "start_year_t2"
	| "last_year_of_accounting_t2"
	| "climate_t2"
	| "moisture_t2"
	| "soc_t2"
	| "cost"
	| "change_rate"
>;

/**
 * Builds the form values. Without `activity` (creation) every field takes the project's defaults;
 * with it (edit) the activity's own overrides win over the project's defaults.
 */
export const createActivityInitialValues = (
	project: ActivityFormProject,
	{ id = 0, activity, defaultChangeRateId }: { id?: number; activity?: ActivitySource; defaultChangeRateId?: number } = {},
): ActivityFormValues => {
	const duration = activity?.duration_t2 ?? project.implementation_years;
	const startYear = activity?.start_year_t2 ?? project.start_year_of_activities;
	const lastYear = activity?.last_year_of_accounting_t2 ?? project.last_year_of_accounting;
	return {
		id,
		activity: {
			name: activity?.name ?? "",
			duration_t2: duration,
			start_year_t2: startYear,
			last_year_of_accounting_t2: lastYear,
			climate_t2: activity?.climate_t2 ?? project.climate,
			moisture_t2: activity?.moisture_t2 ?? project.moisture,
			soc_t2: activity?.soc_t2 ?? null,
			cost: activity ? activity.cost : 0,
			change_rate: activity ? activity.change_rate : defaultChangeRateId,
			capitalization_years: getCapitalizationYears(startYear, duration, lastYear),
		},
	};
};

// --------------------------------------------------------------- payload ---

/**
 * Maps form values to API fields. Values equal to the project's defaults are sent as `null`
 * so the activity keeps inheriting them.
 */
export const toActivityPayloadFields = (activity: ActivityFormActivity, project?: ActivityFormProject | null) => ({
	name: activity.name ?? "",
	duration_t2: !activity.duration_t2 || activity.duration_t2 === project?.implementation_years ? null : activity.duration_t2,
	start_year_t2: !activity.start_year_t2 || activity.start_year_t2 === project?.start_year_of_activities ? null : activity.start_year_t2,
	last_year_of_accounting_t2:
		!activity.last_year_of_accounting_t2 || activity.last_year_of_accounting_t2 === project?.last_year_of_accounting
			? null
			: activity.last_year_of_accounting_t2,
	climate_t2: activity.climate_t2 === project?.climate ? null : activity.climate_t2,
	moisture_t2: activity.moisture_t2 === project?.moisture ? null : activity.moisture_t2,
	soc_t2: activity.soc_t2 ?? null,
	cost: activity.cost ?? 0,
	change_rate: activity.change_rate ?? null,
});
